import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { formatIQD, todayISO } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const METHODS = ["Cash", "Salary Deduction", "Bank Transfer", "Other"];

function RecordPayment() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [empId, setEmpId] = useState("");
  const [summary, setSummary] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [receivedBy, setReceivedBy] = useState("");
  const [ref, setRef] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/users").then((r) => {
      setAdmins(r.data);
      setReceivedBy(user?.email || (r.data[0]?.email || ""));
    });
  }, [user]);
  useEffect(() => {
    if (empId) api.get(`/employees/${empId}/summary`).then((r) => setSummary(r.data));
    else setSummary(null);
  }, [empId]);

  const newBalance = useMemo(() => (summary ? summary.outstanding - Number(amount || 0) : 0), [summary, amount]);

  const submit = async () => {
    if (!empId) return toast.error("Select an employee");
    if (Number(amount) <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      const res = await api.post("/payments", {
        employee_id: empId, payment_date: date, amount: Number(amount), payment_method: method, reference: ref, notes, received_by: receivedBy,
      });
      const alloc = res.data.allocations || [];
      toast.success(`Payment of ${formatIQD(amount)} recorded · allocated to ${alloc.length} order${alloc.length !== 1 ? "s" : ""}`);
      setAmount(""); setRef(""); setNotes("");
      const s = await api.get(`/employees/${empId}/summary`).then((r) => r.data);
      setSummary(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-5 space-y-4">
        <div>
          <Label>Employee</Label>
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger className="mt-1.5" data-testid="pay-emp"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.phone || e.email || ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {summary && (
          <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4" data-testid="pay-current-outstanding">
            <div className="text-xs uppercase text-rose-700 dark:text-rose-300 font-semibold">Current Outstanding</div>
            <div className="heading text-3xl font-extrabold text-rose-700 dark:text-rose-300 mt-1">{formatIQD(summary.outstanding)}</div>
            <div className="text-xs text-muted-foreground mt-1">Unpaid orders: {summary.unpaid_orders_count}</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Payment Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="pay-date" /></div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1.5" data-testid="pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Received By</Label>
          <Select value={receivedBy} onValueChange={setReceivedBy}>
            <SelectTrigger className="mt-1.5" data-testid="pay-received-by"><SelectValue placeholder="Select admin" /></SelectTrigger>
            <SelectContent>
              {admins.map((a) => <SelectItem key={a.id} value={a.email}>{a.name} — {a.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground mt-1">Which admin physically received this money.</div>
        </div>
        <div>
          <Label>Amount (IQD)</Label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 text-lg font-semibold mono" data-testid="pay-amount" />
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {[1000, 5000, 10000, 25000, 50000].map((v) => (
              <button key={v} type="button" onClick={() => setAmount(String(Number(amount || 0) + v))} className="text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700">
                +{v.toLocaleString()}
              </button>
            ))}
            {summary && <button type="button" onClick={() => setAmount(String(summary.outstanding))} className="text-xs px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-950 dark:text-emerald-300">Pay Full</button>}
          </div>
        </div>
        <div><Label>Reference</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} className="mt-1.5" data-testid="pay-ref" /></div>
        <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" /></div>
      </Card>
      <Card className="p-5 space-y-4">
        <div className="heading font-bold">Preview</div>
        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Current outstanding</span><span className="mono font-semibold">{formatIQD(summary?.outstanding || 0)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Payment amount</span><span className="mono font-semibold text-emerald-700">- {formatIQD(amount || 0)}</span></div>
          <div className="border-t pt-2 flex justify-between">
            <span className="font-medium">New balance</span>
            <span className={`heading text-xl font-extrabold ${newBalance > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="pay-new-balance">{formatIQD(Math.max(newBalance, 0))}</span>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">Payments are automatically allocated to the employee's oldest unpaid orders first (FIFO).</div>
        <Button onClick={submit} disabled={saving} data-testid="btn-save-payment" className="w-full bg-emerald-600 hover:bg-emerald-700">
          {saving ? "Saving…" : "Save Payment"}
        </Button>
      </Card>
    </div>
  );
}

function BulkPayment() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [q, setQ] = useState("");
  const [amounts, setAmounts] = useState({});
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState("Cash");
  const [receivedBy, setReceivedBy] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/balances", { params: { only_outstanding: true } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/users").then((r) => {
      setAdmins(r.data);
      setReceivedBy(user?.email || (r.data[0]?.email || ""));
    });
  }, [user]);

  const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));
  const total = filtered.reduce((s, r) => s + Number(amounts[r.id] || 0), 0);
  const count = filtered.filter((r) => Number(amounts[r.id] || 0) > 0).length;

  const submit = async () => {
    const items = Object.entries(amounts).filter(([, v]) => Number(v) > 0).map(([employee_id, amount]) => ({ employee_id, amount: Number(amount) }));
    if (!items.length) return toast.error("Enter at least one amount");
    setSaving(true);
    try {
      await api.post("/payments/bulk", { payment_date: date, payment_method: method, received_by: receivedBy, items });
      toast.success(`Recorded ${items.length} payments totaling ${formatIQD(total)}`);
      setAmounts({}); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex gap-3 flex-wrap items-end">
          <div><Label>Payment Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="bulk-date" /></div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1.5 w-48" data-testid="bulk-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Received By</Label>
            <Select value={receivedBy} onValueChange={setReceivedBy}>
              <SelectTrigger className="mt-1.5 w-56" data-testid="bulk-received-by"><SelectValue placeholder="Select admin" /></SelectTrigger>
              <SelectContent>{admins.map((a) => <SelectItem key={a.id} value={a.email}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-56 relative">
            <Label>Search</Label>
            <Search size={14} className="absolute left-2.5 top-9 text-muted-foreground" />
            <Input placeholder="Filter by name" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 mt-1.5" />
          </div>
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-3">Employee</th>
                <th className="p-3 text-right">Outstanding</th>
                <th className="p-3 text-right">Amount to Pay</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`bulk-row-${r.id}`}>
                  <td className="p-3 font-medium">{r.name}<div className="text-xs text-muted-foreground">{r.phone || r.email || ""}</div></td>
                  <td className="p-3 text-right mono font-semibold text-rose-700">{formatIQD(r.outstanding)}</td>
                  <td className="p-3 text-right w-48"><Input type="number" value={amounts[r.id] || ""} onChange={(e) => setAmounts({ ...amounts, [r.id]: e.target.value })} className="text-right mono" data-testid={`bulk-amt-${r.id}`} /></td>
                  <td className="p-3"><Button variant="outline" size="sm" onClick={() => setAmounts({ ...amounts, [r.id]: r.outstanding })}>Full</Button></td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No employees with outstanding balances.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t bg-slate-900 text-white flex justify-between items-center flex-wrap gap-3">
          <div className="flex gap-6">
            <div><div className="text-xs uppercase text-slate-400">Employees</div><div className="heading text-xl font-bold">{count}</div></div>
            <div><div className="text-xs uppercase text-emerald-300">Total</div><div className="heading text-xl font-bold text-emerald-300">{formatIQD(total)}</div></div>
          </div>
          <Button onClick={submit} disabled={saving} data-testid="btn-bulk-save" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
            {saving ? "Saving…" : "Record Bulk Payment"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function Payments() {
  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <h1 className="heading text-2xl font-extrabold tracking-tight">Payments</h1>
        <p className="text-sm text-muted-foreground">Record payments individually or in bulk. Payments are auto-allocated FIFO.</p>
      </div>
      <Tabs defaultValue="single">
        <TabsList data-testid="pay-tabs">
          <TabsTrigger value="single" data-testid="tab-single">Record Payment</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk Payment</TabsTrigger>
        </TabsList>
        <TabsContent value="single" className="mt-4"><RecordPayment /></TabsContent>
        <TabsContent value="bulk" className="mt-4"><BulkPayment /></TabsContent>
      </Tabs>
    </div>
  );
}
